import unionBy from 'lodash/unionBy'
import moment from 'moment'

import { RestDay, Shift, ShiftsRow } from 'modules/shift_management/lib/types'
import type Holidays from 'modules/shift_management/store/internal/collections/Holidays'
import type Leaves from 'modules/shift_management/store/internal/collections/Leaves'
import type RestDays from 'modules/shift_management/store/internal/collections/RestDays'
import type Shifts from 'modules/shift_management/store/internal/collections/Shifts'
import ShiftsConflicts from 'modules/shift_management/store/internal/collections/ShiftsConflicts'
import type ShiftConflict from 'modules/shift_management/store/internal/models/ShiftConflict'
import View from 'modules/shift_management/store/internal/models/View'

export type StatsContent = {
  shiftConflicts: ShiftsConflicts
  hasDrafts: boolean
  hasDraftsWithoutConflict: boolean
}

export interface ScheduleState {
  view: View
  leaves: Leaves | undefined
  holidays: Holidays | undefined
  restDays: RestDays | undefined
  shiftsToQuery: Shift[]
  newShiftsToQuery: Shift[]
  conflictsCount: number
  hasDrafts: boolean
  hasDraftsWithoutConflict: boolean
  shiftsConflicts: ShiftsConflicts
  isTimeFormat24H: boolean
  statsLoading: boolean
}

export const SOURCE = 'shift_management'

export const WEEK_STATUSES = {
  draft: 'draft',
  published: 'published',
  conflict: 'conflict',
} as const

export type ScheduleDispatch = (arg0: ScheduleAction) => void

export type ScheduleAction =
  | {
      type: 'BOOTSTRAP'
      stats: StatsContent
      leaves: Leaves | undefined
      holidays: Holidays | undefined
      restDays: RestDays | undefined
      shifts: Shifts | undefined
    }
  | {
      type: 'ADD_SHIFTS_ROWS'
      shiftsRows: ShiftsRow[]
    }
  | {
      type: 'ADD_SHIFTS'
      shifts: Shift[]
    }
  | {
      type: 'ADD_REST_DAYS'
      restDays: RestDay[]
    }
  | {
      type: 'RESET_VIEW'
      view: View
    }
  | {
      type: 'PUBLISH_WEEK'
    }
  | {
      type: 'REFRESH_STATS'
      previousShiftsRows: ShiftsRow[]
      shiftsRows: ShiftsRow[]
      stats: StatsContent
    }
  | {
      type: 'SET_STATS_LOADING'
      statsLoading: boolean
    }

export interface ScheduleArgs {
  schedule?: { view: View; leaves?: Leaves }
}

interface InitialScheduleStateArgs {
  args: ScheduleArgs
  isTimeFormat24H: boolean
}

export function initialScheduleState({
  args = {},
  isTimeFormat24H,
}: InitialScheduleStateArgs): ScheduleState {
  const now = moment()
  const view = new View({ date: now, isTimeFormat24H })

  return {
    view,
    leaves: undefined,
    holidays: undefined,
    restDays: undefined,
    // This var is used as workaround to refetch PlannedBreaks or Swapping shifts with GraphQL
    // TODO: Remove this once 'dev_shift_management_graphql_migration' is done
    shiftsToQuery: [],
    newShiftsToQuery: [], // This var is used to refetch coverages or stats.
    conflictsCount: 0,
    hasDrafts: false,
    hasDraftsWithoutConflict: false,
    shiftsConflicts: new ShiftsConflicts([]),
    isTimeFormat24H,
    statsLoading: false,
    ...args.schedule,
  }
}

export default function reducer(state: ScheduleState, action: ScheduleAction): ScheduleState {
  switch (action.type) {
    case 'BOOTSTRAP':
      return {
        ...state,
        leaves: action.leaves ?? state.leaves,
        holidays: action.holidays ?? state.holidays,
        restDays: action.restDays ?? state.restDays,
        hasDrafts: action.stats.hasDrafts,
        hasDraftsWithoutConflict: action.stats.hasDraftsWithoutConflict,
        shiftsToQuery: action.shifts?.shifts || [],
        conflictsCount: action.stats.shiftConflicts.shiftIds.length,
        shiftsConflicts: action.stats.shiftConflicts,
        statsLoading: false,
      }

    case 'REFRESH_STATS': {
      const shiftIds = new Set(action.previousShiftsRows.flatMap((row) => row.shifts.shiftIds))

      const otherShiftConflicts: ShiftConflict[] = state.shiftsConflicts.shift_conflicts.filter(
        (sc) => !shiftIds.has(sc.id) && sc.conflictingShiftsIds.every((id) => !shiftIds.has(id))
      )

      const newShiftsConflicts: ShiftConflict[] = action.stats.shiftConflicts.shift_conflicts
      const shiftsConflicts = unionBy(otherShiftConflicts, newShiftsConflicts, 'id')
      const conflictsCount = shiftsConflicts.length

      return {
        ...state,
        hasDrafts: action.stats.hasDrafts,
        hasDraftsWithoutConflict: action.stats.hasDraftsWithoutConflict,
        conflictsCount: conflictsCount,
        shiftsConflicts: new ShiftsConflicts(shiftsConflicts),
        shiftsToQuery: action.shiftsRows.flatMap((row) => row.shifts.shifts),
        newShiftsToQuery: action.shiftsRows.flatMap((row) => row.shifts.shifts),
      }
    }

    case 'ADD_SHIFTS_ROWS': {
      const shiftsToQuery = action.shiftsRows.flatMap((row) => row.shifts.shifts)
      return { ...state, shiftsToQuery }
    }

    case 'ADD_SHIFTS': {
      return {
        ...state,
        hasDrafts: true,
        shiftsToQuery: action.shifts,
        newShiftsToQuery: action.shifts,
      }
    }

    case 'ADD_REST_DAYS': {
      return { ...state, hasDrafts: true }
    }

    case 'RESET_VIEW':
      return {
        ...state,
        ...initialScheduleState({ args: {}, isTimeFormat24H: state.isTimeFormat24H }),
        view: action.view,
        leaves: state.leaves,
        restDays: state.restDays,
        holidays: state.holidays,
      }
    case 'SET_STATS_LOADING':
      return { ...state, statsLoading: action.statsLoading }
    case 'PUBLISH_WEEK':
      return { ...state, hasDrafts: false }

    default:
      return state
  }
}
